/**
 * AppError.ts
 * Classe d'erreur typée pour l'application
 */
import type { AppError as AppErrorType } from '../types/AppTypes';

/**
 * Classe d'erreur personnalisée pour l'application
 * Permet de typer les erreurs et d'ajouter du contexte
 */
export class AppError extends Error implements AppErrorType {
  public readonly code: string;
  public readonly context?: unknown;
  public readonly timestamp: number;
  public readonly originalError?: Error;

  /**
   * Crée une nouvelle erreur d'application
   *
   * @param code - Code d'erreur unique (ex: 'NETWORK_ERROR', 'SERVER_ERROR', 'TIMEOUT')
   * @param message - Message d'erreur lisible
   * @param context - Contexte additionnel (optionnel)
   * @param originalError - Erreur originale si disponible (optionnel)
   *
   * @example
   * ```typescript
   * throw new AppError('NETWORK_ERROR', 'Impossible de se connecter au serveur', { url: '/api/projects' });
   * ```
   */
  constructor(code: string, message: string, context?: unknown, originalError?: Error) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.message = message;
    this.context = context;
    this.timestamp = Date.now();
    this.originalError = originalError;
    this.stack = originalError?.stack || this.stack;

    // Maintenir la chaîne de prototypes correcte
    Object.setPrototypeOf(this, AppError.prototype);
  }

  /**
   * Crée une AppError depuis une erreur réseau (fetch)
   */
  static fromNetworkError(error: Error, url: string, context?: Record<string, unknown>): AppError {
    return new AppError(
      'NETWORK_ERROR',
      `Erreur réseau lors de la requête vers ${url}: ${error.message}`,
      { url, ...context },
      error
    );
  }

  /**
   * Crée une AppError depuis une erreur HTTP (réponse non-OK)
   */
  static fromHttpError(
    status: number,
    statusText: string,
    url: string,
    responseBody?: unknown
  ): AppError {
    const code = status >= 500 ? 'SERVER_ERROR' : 'CLIENT_ERROR';
    return new AppError(
      code,
      `Erreur HTTP ${status} ${statusText} lors de la requête vers ${url}`,
      { status, statusText, url, responseBody }
    );
  }

  /**
   * Crée une AppError pour un timeout
   */
  static fromTimeout(url: string, timeoutMs: number): AppError {
    return new AppError('TIMEOUT', `La requête vers ${url} a expiré après ${timeoutMs}ms`, {
      url,
      timeoutMs,
    });
  }

  /**
   * Convertit l'erreur en objet JSON
   */
  toJSON(): AppErrorType {
    return {
      code: this.code,
      message: this.message,
      stack: this.stack,
      context: this.context,
      timestamp: this.timestamp,
    };
  }
}
